import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useGarden, makeContainerId } from '../context/GardenContext'
import ContainerVisualizer from '../components/ContainerVisualizer'
import type { Container, ContainerSize, ContainerShape, SunExposure, LocationType } from '../types'
import { CONTAINER_SIZE_INFO } from '../types'

const SHAPES: { value: ContainerShape; label: string; emoji: string }[] = [
  { value: 'round',       label: 'Round Pot',   emoji: '🪣' },
  { value: 'square',      label: 'Square Pot',  emoji: '📦' },
  { value: 'rectangular', label: 'Rectangle',   emoji: '▭' },
  { value: 'window-box',  label: 'Window Box',  emoji: '🪟' },
]

const SUN_OPTIONS: { value: SunExposure; label: string; emoji: string; desc: string }[] = [
  { value: 'full-sun',      label: 'Full Sun',      emoji: '☀️',  desc: '6+ hours direct sun' },
  { value: 'partial-shade', label: 'Part Shade',    emoji: '⛅', desc: '3–6 hours sun' },
  { value: 'full-shade',    label: 'Full Shade',    emoji: '🌑', desc: 'Under 3 hours sun' },
]

const LOCATIONS: { value: LocationType; label: string; emoji: string }[] = [
  { value: 'balcony',    label: 'Balcony',    emoji: '🏢' },
  { value: 'patio',      label: 'Patio',      emoji: '🏡' },
  { value: 'windowsill', label: 'Windowsill', emoji: '🪟' },
  { value: 'indoors',    label: 'Indoors',    emoji: '🏠' },
  { value: 'yard',       label: 'Yard',       emoji: '🌳' },
]

const DEFAULT_FORM = {
  nickname: '',
  size: 'medium' as ContainerSize,
  shape: 'round' as ContainerShape,
  sunExposure: 'full-sun' as SunExposure,
  location: 'patio' as LocationType,
}

export default function ContainerSetup() {
  const { state, dispatch } = useGarden()
  const navigate = useNavigate()
  const [form, setForm] = useState(DEFAULT_FORM)
  const [showForm, setShowForm] = useState(state.containers.length === 0)

  function set<K extends keyof typeof form>(key: K, val: typeof form[K]) {
    setForm(f => ({ ...f, [key]: val }))
  }

  function handleAdd() {
    const container: Container = {
      id: makeContainerId(),
      nickname: form.nickname.trim() || `Container ${state.containers.length + 1}`,
      size: form.size,
      shape: form.shape,
      sunExposure: form.sunExposure,
      location: form.location,
      plantIds: [],
    }
    dispatch({ type: 'ADD_CONTAINER', container })
    setForm(DEFAULT_FORM)
    setShowForm(false)
  }

  function handleRemove(id: string) {
    dispatch({ type: 'REMOVE_CONTAINER', id })
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-8 page-enter">
      <div className="text-center space-y-2">
        <h1 className="font-pixel text-ac-brown text-base">🪴 Your Containers</h1>
        <p className="font-body text-ac-brown-light">Add the pots and planters you have (or plan to get).</p>
      </div>

      {/* Existing containers */}
      {state.containers.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {state.containers.map(c => (
            <div key={c.id} className="ac-card flex flex-col items-center gap-3 relative">
              <ContainerVisualizer container={c} mini />
              <div className="text-center">
                <p className="font-body font-bold text-ac-brown text-sm">{c.nickname}</p>
                <p className="font-body text-xs text-ac-brown-light capitalize">
                  {CONTAINER_SIZE_INFO[c.size].label} · {c.sunExposure.replace('-', ' ')}
                </p>
                <p className="font-body text-xs text-ac-brown-light capitalize">{c.location}</p>
              </div>
              <button
                className="ac-btn-outline text-xs px-3 py-1 text-red-500 border-red-300 hover:bg-red-50"
                onClick={() => handleRemove(c.id)}
              >
                Remove
              </button>
            </div>
          ))}

          {/* Add another tile */}
          <button
            className="ac-card flex flex-col items-center justify-center gap-2 border-dashed hover:bg-ac-bg min-h-[180px]"
            onClick={() => setShowForm(true)}
          >
            <span className="text-4xl text-ac-green">+</span>
            <span className="font-body font-bold text-ac-green text-sm">Add Container</span>
          </button>
        </div>
      )}

      {/* Add form */}
      {showForm && (
        <div className="ac-dialog p-6 space-y-5 max-w-lg mx-auto">
          <h2 className="font-pixel text-ac-brown text-sm">New Container ✨</h2>

          {/* Nickname */}
          <div>
            <label className="font-body font-bold text-ac-brown text-sm block mb-1">Name it (optional)</label>
            <input
              className="ac-input"
              placeholder='e.g. "Sunny Balcony Pot" or "Kitchen Window Box"'
              value={form.nickname}
              onChange={e => set('nickname', e.target.value)}
            />
          </div>

          {/* Size */}
          <div>
            <label className="font-body font-bold text-ac-brown text-sm block mb-2">Pot Size</label>
            <div className="grid grid-cols-2 gap-2">
              {(Object.entries(CONTAINER_SIZE_INFO) as [ContainerSize, typeof CONTAINER_SIZE_INFO[ContainerSize]][]).map(([size, info]) => (
                <button
                  key={size}
                  className={`p-3 rounded-xl border-2 font-body text-left transition-all
                    ${form.size === size ? 'border-ac-brown bg-ac-yellow shadow-ac-sm' : 'border-ac-brown bg-ac-cream hover:bg-ac-bg'}`}
                  onClick={() => set('size', size)}
                >
                  <p className="font-bold text-ac-brown text-sm">{info.label}</p>
                  <p className="text-xs text-ac-brown-light">{info.inches} · crowding guidance around {info.guidancePlants} plant{info.guidancePlants > 1 ? 's' : ''}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Shape */}
          <div>
            <label className="font-body font-bold text-ac-brown text-sm block mb-2">Shape</label>
            <div className="flex flex-wrap gap-2">
              {SHAPES.map(s => (
                <button
                  key={s.value}
                  className={`filter-chip ${form.shape === s.value ? 'active' : ''}`}
                  onClick={() => set('shape', s.value)}
                >
                  {s.emoji} {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Sun */}
          <div>
            <label className="font-body font-bold text-ac-brown text-sm block mb-2">☀️ Sun Exposure at This Spot</label>
            <div className="space-y-2">
              {SUN_OPTIONS.map(s => (
                <button
                  key={s.value}
                  className={`w-full text-left p-3 rounded-xl border-2 font-body transition-all
                    ${form.sunExposure === s.value ? 'border-ac-brown bg-ac-yellow shadow-ac-sm' : 'border-ac-brown bg-ac-cream hover:bg-ac-bg'}`}
                  onClick={() => set('sunExposure', s.value)}
                >
                  <span className="font-bold text-ac-brown">{s.emoji} {s.label}</span>
                  <span className="text-ac-brown-light text-xs ml-2">{s.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="font-body font-bold text-ac-brown text-sm block mb-2">📍 Where will it live?</label>
            <div className="flex flex-wrap gap-2">
              {LOCATIONS.map(l => (
                <button
                  key={l.value}
                  className={`filter-chip ${form.location === l.value ? 'active' : ''}`}
                  onClick={() => set('location', l.value)}
                >
                  {l.emoji} {l.label}
                </button>
              ))}
            </div>
            {form.location === 'balcony' && (
              <p className="font-body text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mt-2">
                ⚠️ Balcony tip: Check your building's weight limit. Large pots filled with wet soil can be very heavy!
              </p>
            )}
          </div>

          {/* Drainage reminder */}
          <div className="bg-ac-bg rounded-xl p-3 font-body text-xs text-ac-brown">
            💡 <strong>Drainage tip:</strong> Make sure your pot has holes in the bottom! No drainage = root rot. Place a saucer underneath to protect surfaces.
          </div>

          <div className="flex gap-2">
            <button className="ac-btn-outline flex-1" onClick={() => setShowForm(false)}>Cancel</button>
            <button className="ac-btn-green flex-1" onClick={handleAdd}>Add Container 🪴</button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {state.containers.length === 0 && !showForm && (
        <div className="text-center space-y-4 py-8">
          <p className="text-5xl">🪴</p>
          <p className="font-body text-ac-brown-light">No containers yet — add your first one!</p>
          <button className="ac-btn-green" onClick={() => setShowForm(true)}>+ Add a Container</button>
        </div>
      )}

      {/* Next step */}
      {state.containers.length > 0 && (
        <div className="flex justify-between items-center pt-4 border-t-2 border-dashed border-ac-bg">
          <p className="font-body text-sm text-ac-brown-light">
            {state.containers.length} container{state.containers.length !== 1 ? 's' : ''} ready
          </p>
          <button className="ac-btn-green" onClick={() => navigate('/browse')}>
            Browse Plants →
          </button>
        </div>
      )}
    </div>
  )
}
